'use client';

import { FC } from 'react';

import { getRoute } from '@ravago/shared/next-data-access';
import { BrandCard as BrandCardModel } from '@ravago/shared/page-data/models/elements';
import { DynamicPageUrl } from '@ravago/shared/page-data/models/utils';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { BrandCard } from '@ravago/shared/radiance/components/card/components/BrandCard/BrandCard';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { useTranslations } from '@ravago/shared/translations/client';

import { useConfigContext } from '../../../../hooks/use-config-context';
import { ConsumerWrapper } from '../../consumer-wrapper/consumer-wrapper';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';
import { MockLink } from '../../mock-link/mock-link';

interface Props {
   showAllBrandsButton: boolean;
   locale: string;
   isConsumer: boolean;
   baseRoute?: string;
   productsOverviewRoute: DynamicPageUrl | null;
   brandCards: BrandCardModel[];
   urlQuery?: string;
}

export const BrandList: FC<Props> = ({ locale, isConsumer, showAllBrandsButton, baseRoute, productsOverviewRoute, brandCards = [], urlQuery }) => {
   const translate = useTranslations();
   const configContext = useConfigContext();

   return (
      <>
         <Grid useContainerQuery={!isConsumer}>
            {brandCards.map((brand) => (
               <GridCol key={brand.id} useContainerQuery={!isConsumer} spanDesktop={3} spanTablet={6} spanPhone={4}>
                  <BrandCard
                     href={getRoute(locale, baseRoute, brand.slug, undefined, configContext?.useLegacyDigitalPlatformRoutes)}
                     target="_self"
                     noFollow={brand.noFollow}
                     name={brand.name}
                     producer={brand.producer.name ?? ''}
                     logo={brand.producer.logo ?? ''}
                     excerpt={brand.excerpt ?? ''}
                     polymerTypes={brand.polymerTypes}
                     productCount={translate('products-total', { total: brand.numberOfProducts })}
                     fillHeight
                  />
               </GridCol>
            ))}
         </Grid>

         {showAllBrandsButton && (
            <ConsumerWrapper
               isConsumer={isConsumer}
               renderConsumer={(children) => (
                  <DynamicPageLink
                     id={`dynamic-link__brand-list__${urlQuery}`}
                     underline="none"
                     slug={`?tab=brands&${urlQuery}`}
                     baseRoute={productsOverviewRoute}
                     locale={locale}
                  >
                     {children}
                  </DynamicPageLink>
               )}
               renderProducer={(children) => <MockLink underline="none">{children}</MockLink>}
            >
               <Button variant="tertiary" trailingIcon="long-arrow-right">
                  {translate('related-brands-2.all-brands')}
               </Button>
            </ConsumerWrapper>
         )}
      </>
   );
};
